import {RefObject, useCallback, useEffect, useMemo, useRef} from 'react';
import {Mesh, ShaderMaterial, Texture} from 'three';
import {useFrame, useThree} from '@react-three/fiber';
import {useFBO} from '@react-three/drei';
import {useXR} from '@react-three/xr';

type XRWebGLBindingExtType = XRWebGLBinding & {
  getCameraImage: (view: unknown) => WebGLTexture;
};

type CameraFeedType = {
  tex: Texture | null;
  glTex: WebGLTexture | null;
  framebuffer: WebGLFramebuffer | null;
  isInitialized: boolean;
};

interface IUseFrameRendererProps {
  compositeMeshRef: RefObject<Mesh | null>;
}

const useFrameRenderer = ({compositeMeshRef}: IUseFrameRendererProps) => {
  const session = useXR(s => s.session);
  const gl = useThree(s => s.gl);
  const ctx = useMemo(() => gl.getContext(), [gl]);
  const fbo = useFBO(gl.domElement.width, gl.domElement.height);

  const glBindingRef = useRef<XRWebGLBindingExtType>(null);
  const refSpaceRef = useRef<XRReferenceSpace>(null);
  const shaderMatRef = useRef<ShaderMaterial>(null);

  const cameraFeedRef = useRef<CameraFeedType>({
    tex: null,
    glTex: null,
    framebuffer: null,
    isInitialized: false,
  });

  const initConfig = useCallback(async (session: XRSession | undefined, ctx: WebGLRenderingContext) => {
    if (session && ctx) {
      glBindingRef.current = new XRWebGLBinding(session, ctx) as XRWebGLBindingExtType;
      refSpaceRef.current = await session.requestReferenceSpace('viewer');
      shaderMatRef.current = compositeMeshRef.current?.material as ShaderMaterial;
      cameraFeedRef.current = {
        tex: new Texture(),
        glTex: ctx.createTexture(),
        framebuffer: ctx.createFramebuffer(),
        isInitialized: true,
      };
    }
  }, []);

  useEffect(() => {
    initConfig(session, ctx);
  }, [session, ctx, initConfig]);

  const getCameraFeed = useCallback((ctx: WebGLRenderingContext, frame: XRFrame) => {
    if (!refSpaceRef.current || !glBindingRef.current) {
      return;
    }
    const pose = frame.getViewerPose(refSpaceRef.current);
    if (!pose) {
      return;
    }
    for (const view of pose.views) {
      // @ts-expect-error
      const viewCamera = view.camera;
      if (!viewCamera) {
        continue;
      }
      const cameraTexGl = glBindingRef.current.getCameraImage(viewCamera);
      const prevFramebuffer = ctx.getParameter(ctx.FRAMEBUFFER_BINDING) as WebGLFramebuffer;

      ctx.bindFramebuffer(ctx.FRAMEBUFFER, cameraFeedRef.current.framebuffer);
      ctx.framebufferTexture2D(ctx.FRAMEBUFFER, ctx.COLOR_ATTACHMENT0, ctx.TEXTURE_2D, cameraTexGl, 0);
      ctx.bindTexture(ctx.TEXTURE_2D, cameraFeedRef.current.glTex);
      ctx.copyTexImage2D(ctx.TEXTURE_2D, 0, ctx.RGBA, 0, 0, viewCamera.width, viewCamera.height, 0);
      ctx.generateMipmap(ctx.TEXTURE_2D);
      ctx.texParameteri(ctx.TEXTURE_2D, ctx.TEXTURE_MAG_FILTER, ctx.LINEAR);

      const texProps = gl.properties.get(cameraFeedRef.current.tex);
      (texProps as any).__webglTexture = cameraFeedRef.current.glTex;

      ctx.bindFramebuffer(ctx.FRAMEBUFFER, prevFramebuffer);
      break;
    }
  }, []);

  useFrame(({gl, scene, camera}, _d, frame) => {
    if (!frame || !compositeMeshRef.current || !shaderMatRef.current || !cameraFeedRef.current.isInitialized) {
      return;
    }
    getCameraFeed(ctx, frame);
    shaderMatRef.current.uniforms.u_camera.value = cameraFeedRef.current.tex;
    shaderMatRef.current.uniforms.u_scene.value = fbo.texture;
    compositeMeshRef.current.visible = false;
    const xrRenderTarget = gl.getRenderTarget();
    gl.setRenderTarget(fbo);
    gl.render(scene, camera);
    gl.setRenderTarget(xrRenderTarget);
    compositeMeshRef.current.visible = true;
    gl.render(compositeMeshRef.current, camera);
    // 1 to take control of rendering by self and prevent redundant rendering on xr layer
  }, 1);
};

export default useFrameRenderer;
